import type { AccountLocationInput } from '@important-not-important-projects/wcid-account-core'
import type { ActionToolAccountWithId, UserLocation } from '../types/account-types'
import type { LocationResponse } from '../types/civic-info-types'

export const MAX_SAVED_LOCATIONS = 5

const normalizeAddress = (value?: string | null) => (value ?? '').trim().toLowerCase()

// The account's saved locations, home first. Before an account has loaded its
// locations (or on an older snapshot) this falls back to the single legacy
// profile location as the home, so callers always get something consistent.
export const getAccountLocations = (account?: ActionToolAccountWithId | null): UserLocation[] => {
  const locations = account?.user?.locations
  if (locations && locations.length > 0) {
    return locations
  }

  const legacyText = account?.user?.profile?.private?.location
  if (!legacyText) {
    return []
  }

  const legacyData = account?.user?.profile?.private?.location_data
  return [
    {
      id: 'legacy-home',
      label: 'Home',
      isHome: true,
      homeConfirmedAt: null,
      locationText: legacyText,
      locationData: {
        country: legacyData?.country ?? null,
        state: legacyData?.state ?? null,
        city: legacyData?.city ?? null,
        zipcode: legacyData?.zipcode ?? null
      },
      stateAbbr: null,
      countyFips: null,
      congressionalDistrict: null,
      lat: null,
      lng: null,
      createdAt: null,
      updatedAt: null
    }
  ]
}

export const getHomeLocation = (account?: ActionToolAccountWithId | null) =>
  getAccountLocations(account).find(location => location.isHome)

// The save payload for an address the user picked, from its geocoding result.
export const buildLocationInputFromGeocode = (
  locationText: string,
  geocode?: LocationResponse | null
): AccountLocationInput => ({
  locationText,
  locationData: {
    country: geocode?.country?.long_name ?? null,
    state: geocode?.state?.long_name ?? null,
    city: geocode?.city?.long_name ?? null,
    zipcode: geocode?.postalcode?.long_name ?? null
  },
  stateAbbr: geocode?.state?.short_name ?? null,
  lat: typeof geocode?.lat === 'number' ? geocode.lat : null,
  lng: typeof geocode?.lng === 'number' ? geocode.lng : null
})

// Every saved location's country / state / city, deduplicated, for
// recommendations. "United States" is left out of countries (as before): it
// matches nearly every national action and would swamp local ones.
export const getSearchableLocationParams = (locations: UserLocation[]) => {
  const unique = (values: (string | null | undefined)[]) =>
    Array.from(
      new Set(values.map(value => value?.trim()).filter((value): value is string => Boolean(value)))
    )

  return {
    countries: unique(locations.map(location => location.locationData.country)).filter(
      country => country !== 'United States'
    ),
    states: unique(locations.map(location => location.locationData.state)),
    cities: unique(locations.map(location => location.locationData.city))
  }
}

// Whether an address the user typed is one of their saved locations other than
// home (representatives are only looked up for the home).
export const isSavedNonHomeAddress = (
  account: ActionToolAccountWithId | null | undefined,
  address?: string | null
) => {
  const target = normalizeAddress(address)
  if (!target) {
    return false
  }

  return getAccountLocations(account).some(
    location => !location.isHome && normalizeAddress(location.locationText) === target
  )
}
